import { DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import type { PersonaId } from "@/lib/personas/types";
import { isIndexableLocale } from "@/lib/seo/indexing";
import { isContentField, isContentStaticField, isContentTextField, isRecord } from "./schema";
import { assertValidContentEntry, ContentValidationError, validateRequiredResolvedContent } from "./validation";
import type {
  ContentEntry,
  ContentObject,
  ContentPersonaScope,
  ContentSurface,
  ContentValue,
  DeepContentPartial,
  FieldProvenance,
  ResolvedContent,
  UnwrapContentValue,
} from "./types";

type ResolveContentInput<TContent extends ContentObject> = {
  entry: ContentEntry<TContent>;
  locale: AppLocale;
  persona?: ContentPersonaScope;
  surface?: ContentSurface;
  indexable?: boolean;
  allowPreviewFallback?: boolean;
};

type ResolveLayer = {
  source: FieldProvenance["source"];
  locale: AppLocale;
  persona?: PersonaId;
  surface?: ContentSurface;
};

type Draft = {
  value: unknown;
  provenance: Record<string, FieldProvenance>;
};

export function resolveContent<TContent extends ContentObject>({
  entry,
  locale,
  persona = "none",
  surface = "canonical",
  indexable = isIndexableLocale(locale),
  allowPreviewFallback = !indexable,
}: ResolveContentInput<TContent>): ResolvedContent<TContent> {
  assertValidContentEntry(entry);

  const draft: Draft = { value: {}, provenance: {} };
  applyLayer(draft, entry.base, { source: "base", locale: entry.baseLocale });

  if (persona !== "none") {
    const personaOverride = entry.personaOverrides?.[persona];
    if (personaOverride) {
      applyLayer(draft, personaOverride, { source: "persona", locale: entry.baseLocale, persona });
    }
  }

  const localeOverride = entry.localeOverrides?.[locale];
  if (localeOverride) {
    applyLayer(draft, localeOverride, { source: "locale", locale });
  }

  if (persona !== "none") {
    const localePersonaOverride = entry.localePersonaOverrides?.[locale]?.[persona];
    if (localePersonaOverride) {
      applyLayer(draft, localePersonaOverride, { source: "locale-persona", locale, persona });
    }
  }

  assertResolved(entry, draft, locale, indexable, allowPreviewFallback);

  const surfaceOverride = entry.surfaceOverrides?.[surface];
  if (surfaceOverride) {
    applyLayer(draft, surfaceOverride, { source: "surface", locale, surface });
  }

  const deviceOverride = surface === "mobile" ? entry.deviceOverrides?.mobile : surface === "canonical" ? entry.deviceOverrides?.desktop : undefined;
  if (deviceOverride) {
    applyLayer(draft, deviceOverride, { source: "device", locale, surface });
  }

  assertResolved(entry, draft, locale, indexable, allowPreviewFallback);

  return {
    entry,
    locale,
    persona,
    surface,
    indexable,
    content: unwrapContentValue(draft.value) as UnwrapContentValue<TContent>,
    provenance: draft.provenance,
  };
}

function applyLayer(draft: Draft, layer: DeepContentPartial<ContentObject>, metadata: ResolveLayer): void {
  draft.value = mergeValue(draft.value, layer, draft.provenance, metadata, []);
}

function mergeValue(
  current: unknown,
  incoming: unknown,
  provenance: Record<string, FieldProvenance>,
  metadata: ResolveLayer,
  path: string[],
): unknown {
  if (incoming === undefined) return current;

  if (isContentField(incoming) || Array.isArray(incoming) || !isRecord(incoming)) {
    recordProvenance(incoming, provenance, metadata, path);
    return incoming;
  }

  const next: Record<string, unknown> = isRecord(current) && !isContentField(current) ? { ...current } : {};

  for (const [key, value] of Object.entries(incoming)) {
    next[key] = mergeValue(next[key], value, provenance, metadata, [...path, key]);
  }

  return next;
}

function recordProvenance(
  value: unknown,
  provenance: Record<string, FieldProvenance>,
  metadata: ResolveLayer,
  path: string[],
): void {
  if (isContentTextField(value)) {
    const fieldPath = path.join(".");
    provenance[fieldPath] = {
      path: fieldPath,
      ...metadata,
      status: value._status,
      lang: value._lang,
      usedFallback: value._lang !== metadata.locale || (metadata.source === "base" && metadata.locale === DEFAULT_LOCALE),
    };
    return;
  }

  if (isContentStaticField(value)) {
    const fieldPath = path.join(".");
    provenance[fieldPath] = {
      path: fieldPath,
      ...metadata,
      usedFallback: false,
    };
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => recordProvenance(item, provenance, metadata, [...path, String(index)]));
    return;
  }

  if (isRecord(value)) {
    for (const [key, item] of Object.entries(value)) {
      recordProvenance(item, provenance, metadata, [...path, key]);
    }
  }
}

function unwrapContentValue(value: unknown): unknown {
  if (isContentTextField(value) || isContentStaticField(value)) {
    return value.value;
  }

  if (Array.isArray(value)) {
    return value.map(unwrapContentValue);
  }

  if (isRecord(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, unwrapContentValue(item)]));
  }

  return value as ContentValue;
}

function assertResolved(
  entry: ContentEntry,
  draft: Draft,
  locale: AppLocale,
  indexable: boolean,
  allowPreviewFallback: boolean,
): void {
  const content = unwrapContentValue(draft.value);
  const issues = validateRequiredResolvedContent({
    entry,
    content,
    provenance: draft.provenance,
    locale,
    indexable,
    allowPreviewFallback,
  });

  if (issues.length > 0) {
    throw new ContentValidationError(`Content entry ${entry.key} failed required content validation.`, issues);
  }
}
